import apiClient from './apiClient';

// ============================================================
// Types
// ============================================================

export interface FileEntry {
  path: string;
  lines: number;
}

export interface EndpointEntry {
  method: string;
  path: string;
  description: string;
}

export interface SolidScoreEntry {
  score: number;
  note: string;
}

export type SolidDetail = Record<string, SolidScoreEntry>;

export interface SubmoduleItem {
  id: number;
  formId: string;
  submoduleId: string;
  name: string;
  status: string;
  solidScore: number;
  solidDetail: SolidDetail | null;
  e2eStatus: string;
  files: FileEntry[] | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ModuleListItem {
  formId: string;
  name: string;
  specDir: string;
  auditScore: number;
  specSizeKb: number;
  dependencyLevel: number | null;
  backendStatus: string;
  beSolidScore: number;
  frontendStatus: string;
  feSolidScore: number;
  e2eStatus: string;
  status: string;
  submoduleCount: number;
  updatedAt: string;
}

export interface ModuleDetail {
  formId: string;
  name: string;
  specDir: string;
  auditScore: number;
  dependencyLevel: number | null;
  backendStatus: string;
  beSolidScore: number;
  beSolidDetail: SolidDetail | null;
  frontendStatus: string;
  feSolidScore: number;
  feSolidDetail: SolidDetail | null;
  e2eStatus: string;
  status: string;
  files: FileEntry[] | null;
  endpoints: EndpointEntry[] | null;
  backendCompletedAt: string | null;
  frontendCompletedAt: string | null;
  e2eCompletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  submodules: SubmoduleItem[] | null;
}

export interface TestResult {
  testName: string;
  pass: boolean;
  durationMs: number;
  package?: string;
}

export interface TestRun {
  id: number;
  formId: string;
  submoduleId: string | null;
  category: string;
  format: string;
  totalTests: number;
  passCount: number;
  failCount: number;
  totalDurationMs: number;
  results: TestResult[];
  runAt: string;
}

export interface TrendPoint {
  runAt: string;
  category: string;
  formId: string;
  totalTests: number;
  passCount: number;
  failCount: number;
  totalDurationMs: number;
}

export interface StatsModules {
  total: number;
  done: number;
  wip: number;
  bug: number;
  blocked: number;
  notStarted: number;
  completionRate: number;
}

export interface StatsTestCategory {
  totalTests: number;
  passCount: number;
  failCount: number;
  lastRunAt: string | null;
}

export interface StatsData {
  modules: StatsModules;
  tests: {
    backend: StatsTestCategory;
    e2e: StatsTestCategory;
  };
}

export interface ModuleListResponse {
  data: ModuleListItem[];
  total: number;
  page: number;
  size: number;
}

export interface TestRunListResponse {
  data: TestRun[];
  total: number;
  page: number;
  size: number;
}

export interface FilterState {
  search: string;
  status: string;
  page: number;
  size: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
}

export interface TestRunFilters {
  page: number;
  size: number;
  formId?: string;
  category?: string;
  failOnly?: boolean;
}

export interface TrendFilters {
  formId?: string;
  category?: string;
  limit?: number;
}

// ============================================================
// Chain Types
// ============================================================

export interface ChainListItem {
  chainId: string;
  chainName: string;
  businessDomain: string;
  startFormId: string;
  currentStatus: string;
  evidenceLevel: string;
  requiresWave0: boolean;
  sharedBase: string[];
  updatedAt: string;
  nodeCount: number;
  readyCount: number;
  inProgressCount: number;
  blockedCount: number;
  approvalCount: number;
}

export interface ChainNodeDep {
  chainId: string;
  nodeId: string;
  dependsOnNode: string;
}

export interface ChainNode {
  nodeId: string;
  chainId: string;
  nodeType: string;
  layer: string;
  label: string;
  tableName: string;
  module: string;
  formId: string;
  apiPrefix: string;
  action: string;
  purpose: string;
  businessMeaning: string;
  buildStrategy: string;
  dependencyLevel: number;
  status: string;
  evidenceLevel: string;
  requiresApproval: boolean;
  reviewPolicy: Record<string, unknown>;
  targetColumns: unknown[];
  extraMeta: Record<string, unknown>;
  updatedAt: string;
  dependsOn: ChainNodeDep[];
}

export interface ChainDetail {
  chainId: string;
  chainName: string;
  businessDomain: string;
  businessGoal: string;
  startFormId: string;
  currentStatus: string;
  evidenceLevel: string;
  sharedBase: string[];
  requiresWave0: boolean;
  sourceFile: string;
  updatedAt: string;
  nodes: ChainNode[];
}

export interface ChainRun {
  runId: number;
  chainId: string;
  runType: string;
  requestedBy: string;
  result: string;
  summary: string;
  moduleScore: number | null;
  chainScore: number | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface ChainStats {
  total: number;
  liveVerified: number;
  integrationVerified: number;
  partiallyConnected: number;
  modulesExist: number;
  notStarted: number;
}

export interface ChainListResponse {
  data: ChainListItem[];
  total: number;
  page: number;
  size: number;
}

export interface ChainFilters {
  search: string;
  domain: string;
  status: string;
  evidence: string;
  formId: string;
  page: number;
  size: number;
}

// ============================================================
// Flow Observe Types (資料流觀測)
// ============================================================

export type ObserveOp = 'I' | 'U' | 'D';

export interface ObserveWindow {
  id: number;
  chainId: string;
  scenarioId: string;
  docPrefix: string;
  status: 'active' | 'stopped';
  startedAt: string;
  stoppedAt?: string | null;
  notes?: string | null;
}

export interface ObserveCurrentResponse {
  window: ObserveWindow | null;
  auditCount: number;
}

export interface ObserveAuditItem {
  id: number;
  tableName: string;
  op: ObserveOp;
  at: string;
  oldRow: Record<string, unknown> | null;
  newRow: Record<string, unknown> | null;
  changedColumns: string[];
}

export interface ObserveAuditsResponse {
  total: number;
  items: ObserveAuditItem[];
}

export interface ObserveRadarEntry {
  table: string;
  inserts: number;
  updates: number;
  deletes: number;
  isNoise: boolean;
  isWhitelisted: boolean;
}

export interface ObserveAuditSummaryEntry {
  table: string;
  inserts: number;
  updates: number;
  deletes: number;
}

export interface ObserveExpectedWrite {
  table: string;
  class: string;
  ops: string[];
  keyColumns: string[];
  note: string;
}

export interface ObserveHandoffCheck {
  id: string;
  name: string;
  method: string;
  urlTemplate: string;
  assertContains: string;
  when: string;
}

export interface ObserveExpectedJson {
  chainId: string;
  whitelistTables: string[];
  expectedWrites: ObserveExpectedWrite[];
  handoffChecks: ObserveHandoffCheck[];
}

export interface ObserveComparisonMatched {
  table: string;
  expectedOps: string[];
  actualOps: string[];
}

export interface ObserveComparisonMissing {
  table: string;
  expectedOps: string[];
  note: string;
}

export interface ObserveComparisonUnexpected {
  table: string;
  source: string;
  inserts: number;
  updates: number;
  deletes: number;
}

export interface ObserveComparison {
  matched: ObserveComparisonMatched[];
  missing: ObserveComparisonMissing[];
  unexpected: ObserveComparisonUnexpected[];
}

export interface ObserveReceipt {
  window: ObserveWindow;
  radar: ObserveRadarEntry[];
  auditSummary: ObserveAuditSummaryEntry[];
  expected: ObserveExpectedJson;
  comparison: ObserveComparison;
}

export interface ObserveHandoffResult {
  checkId: string;
  name: string;
  url: string;
  httpStatus: number;
  pass: boolean;
  detail: string;
}

// ============================================================
// Chain Cockpit Types (鏈駕駛艙)
// ============================================================

export interface CockpitChainInfo {
  chainId: string;
  chainName: string;
  businessDomain: string;
  currentStatus: string;
  evidenceLevel: string;
}

export interface CockpitTableConfig {
  table: string;
  role: string;
  label: string;
  docColumn?: string;
  statusColumns?: string[];
  displayColumns: string[];
  note?: string;
}

export interface CockpitGroupConfig {
  formId: string;
  label: string;
  tables: CockpitTableConfig[];
}

export interface CockpitSideEffectConfig {
  table: string;
  label: string;
  expectWhen: string;
  docColumn?: string;
  via?: string;
  displayColumns: string[];
}

export interface CockpitTraceConfig {
  docInputLabel: string;
  docMaxLen: number;
  groups: CockpitGroupConfig[];
  sideEffects: CockpitSideEffectConfig[];
}

export interface CockpitConfigResponse {
  chain: CockpitChainInfo;
  hasTrace: boolean;
  trace: CockpitTraceConfig | null;
}

export type CockpitRow = Record<string, unknown>;

export interface CockpitTableResult {
  table: string;
  role: string;
  label: string;
  count: number;
  statusValues: Record<string, unknown> | null;
  rows: CockpitRow[];
  note?: string;
}

export interface CockpitGroupResult {
  formId: string;
  label: string;
  tables: CockpitTableResult[];
}

export interface CockpitSideEffectResult {
  table: string;
  label: string;
  expectWhen: string;
  count: number;
  rows: CockpitRow[];
  viaInfo: string | null;
}

export interface CockpitTraceResponse {
  docNo: string;
  groups: CockpitGroupResult[];
  sideEffects: CockpitSideEffectResult[];
}

// ============================================================
// API methods
// ============================================================

export const devApi = {
  // Stats
  async getStats(): Promise<StatsData> {
    const res = await apiClient.get('/api/dev/stats');
    return res.data.data;
  },

  // Modules
  async getModules(params: {
    page?: number;
    size?: number;
    status?: string;
    search?: string;
    sortBy?: string;
    order?: string;
  }): Promise<ModuleListResponse> {
    const res = await apiClient.get('/api/dev/modules', { params });
    return res.data;
  },

  async getModule(formId: string): Promise<ModuleDetail> {
    const res = await apiClient.get(`/api/dev/modules/${encodeURIComponent(formId)}`);
    return res.data.data;
  },

  async updateModule(formId: string, updates: Record<string, unknown>): Promise<ModuleDetail> {
    const res = await apiClient.put(`/api/dev/modules/${encodeURIComponent(formId)}`, updates);
    return res.data.data;
  },

  // Submodules
  async getSubmodules(formId: string): Promise<SubmoduleItem[]> {
    const res = await apiClient.get(`/api/dev/modules/${encodeURIComponent(formId)}/submodules`);
    return res.data.data;
  },

  // Test Runs
  async getTestRuns(params: {
    page?: number;
    size?: number;
    formId?: string;
    category?: string;
    failOnly?: boolean;
  }): Promise<TestRunListResponse> {
    const res = await apiClient.get('/api/dev/test-runs', { params });
    return res.data;
  },

  // Test Trend
  async getTestTrend(params: {
    formId?: string;
    category?: string;
    limit?: number;
  }): Promise<TrendPoint[]> {
    const res = await apiClient.get('/api/dev/test-runs/trend', { params });
    return res.data.data;
  },

  // Import
  async importModules(modules: Partial<ModuleListItem>[]): Promise<{ imported: number; created: number; updated: number }> {
    const res = await apiClient.post('/api/dev/import/modules', { modules });
    return res.data.data;
  },

  // Agent report push APIs
  async pushBackendReport(data: Record<string, unknown>): Promise<unknown> {
    const res = await apiClient.post('/api/dev/report/backend', data);
    return res.data.data;
  },

  async pushSolidReport(data: Record<string, unknown>): Promise<unknown> {
    const res = await apiClient.post('/api/dev/report/solid', data);
    return res.data.data;
  },

  async pushE2EReport(data: Record<string, unknown>): Promise<unknown> {
    const res = await apiClient.post('/api/dev/report/e2e', data);
    return res.data.data;
  },

  async pushSubmoduleReport(data: Record<string, unknown>): Promise<unknown> {
    const res = await apiClient.post('/api/dev/report/submodule', data);
    return res.data.data;
  },

  async pushFixReport(data: Record<string, unknown>): Promise<unknown> {
    const res = await apiClient.post('/api/dev/report/fix', data);
    return res.data.data;
  },

  // Chains
  async getChains(params: {
    page?: number;
    size?: number;
    search?: string;
    domain?: string;
    status?: string;
    evidence?: string;
    formId?: string;
  }): Promise<ChainListResponse> {
    const res = await apiClient.get('/api/dev/chains', { params });
    return res.data;
  },

  async getChain(chainId: string): Promise<ChainDetail> {
    const res = await apiClient.get(`/api/dev/chains/${encodeURIComponent(chainId)}`);
    return res.data.data;
  },

  async getChainStats(): Promise<ChainStats> {
    const res = await apiClient.get('/api/dev/chains/stats');
    return res.data.data;
  },

  async getChainRuns(chainId: string, params?: { page?: number; size?: number }): Promise<{ data: ChainRun[]; total: number; page: number; size: number }> {
    const res = await apiClient.get(`/api/dev/chains/${encodeURIComponent(chainId)}/runs`, { params });
    return res.data;
  },

  // ============================================================
  // Flow Observe (資料流觀測)
  // ============================================================

  async observeStart(body: {
    chainId: string;
    scenarioId: string;
    docPrefix: string;
    notes?: string;
  }): Promise<{ window: ObserveWindow }> {
    const res = await apiClient.post('/api/dev/observe/start', body);
    return res.data;
  },

  async observeStop(windowId: number): Promise<{ window: ObserveWindow }> {
    const res = await apiClient.post('/api/dev/observe/stop', { windowId });
    return res.data;
  },

  async observeCurrent(): Promise<ObserveCurrentResponse> {
    const res = await apiClient.get('/api/dev/observe/current');
    return res.data;
  },

  async observeAudits(
    windowId: number,
    params?: { table?: string; page?: number; size?: number }
  ): Promise<ObserveAuditsResponse> {
    const res = await apiClient.get(`/api/dev/observe/windows/${windowId}/audits`, { params });
    return res.data;
  },

  async observeReceipt(windowId: number): Promise<ObserveReceipt> {
    const res = await apiClient.get(`/api/dev/observe/windows/${windowId}/receipt`);
    return res.data;
  },

  async observeHandoff(windowId: number, results: ObserveHandoffResult[]): Promise<{ ok: boolean }> {
    const res = await apiClient.post(`/api/dev/observe/windows/${windowId}/handoff`, { results });
    return res.data;
  },

  async observeExpected(chainId: string): Promise<ObserveExpectedJson> {
    const res = await apiClient.get(`/api/dev/observe/expected/${encodeURIComponent(chainId)}`);
    return res.data;
  },

  // ============================================================
  // Chain Cockpit (鏈駕駛艙) — 回應不包 data wrapper
  // ============================================================

  async getCockpitConfig(chainId: string): Promise<CockpitConfigResponse> {
    const res = await apiClient.get(`/api/dev/cockpit/${encodeURIComponent(chainId)}/config`);
    return res.data;
  },

  async getCockpitTrace(chainId: string, docNo: string): Promise<CockpitTraceResponse> {
    const res = await apiClient.get(`/api/dev/cockpit/${encodeURIComponent(chainId)}/trace`, {
      params: { docNo },
    });
    return res.data;
  },
};
